import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { initials } from '../lib/format';

// ------------------------------------------------------------------ toasts

type Toast = { id: number; message: string; tone: 'info' | 'error' | 'success' };
const ToastCtx = createContext<(message: string, tone?: Toast['tone']) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((message: string, tone: Toast['tone'] = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, message, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 5000);
  }, []);

  return (
    <ToastCtx.Provider value={push}>
      {children}
      {/* aria-live para leitor de tela anunciar sem roubar o foco */}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast--${t.tone}`}>
            <span>{t.message}</span>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export const useToast = () => useContext(ToastCtx);

// ------------------------------------------------------------------ marca

/**
 * Monograma VJ + "CONSULTING" em texto, igual à landing (vj.dev.br).
 *
 * Não usa os SVGs de lockup do pacote de marca: eles já trazem as letras "VJ" junto do
 * texto, e ao lado do monograma a marca aparecia duplicada. O gradiente vem de variáveis
 * CSS (--mk-*), que o tema escuro troca por tons claros.
 */
export function Logo({
  variant = 'horizontal',
  height = 28,
}: {
  variant?: 'horizontal' | 'stacked';
  height?: number;
}) {
  // useId gera ":r0:"; os dois-pontos quebram a referência url(#...) do gradiente
  const grad = `vjGrad${useId().replace(/:/g, '')}`;
  return (
    <span className={`vj-logo vj-logo--${variant}`} role="img" aria-label="VJ Consulting">
      <svg
        className="vj-logo__mark"
        viewBox="0 0 128 128"
        fill="none"
        style={{ height, width: height }}
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <linearGradient id={grad} x1="18" y1="28" x2="110" y2="100" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="var(--mk-a)" />
            <stop offset="0.55" stopColor="var(--mk-b)" />
            <stop offset="1" stopColor="var(--mk-c)" />
          </linearGradient>
        </defs>
        <path
          d="M22 34 L40 90 L58 34"
          stroke={`url(#${grad})`}
          strokeWidth="13"
          strokeLinecap="round"
          strokeLinejoin="miter"
          strokeMiterlimit="12"
        />
        <path
          d="M100 34 L100 74 Q100 90 82 90 Q70 90 70 78"
          stroke={`url(#${grad})`}
          strokeWidth="13"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className="vj-logo__word" aria-hidden="true">
        Consulting
      </span>
    </span>
  );
}

// ------------------------------------------------------------------ básicos

export function Avatar({
  name,
  src,
  size = 32,
}: {
  name: string;
  src?: string | null;
  size?: number;
}) {
  const style = { width: size, height: size, fontSize: size * 0.34 };
  if (src) {
    return <img className="avatar" style={style} src={src} alt="" referrerPolicy="no-referrer" />;
  }
  return (
    <span className="avatar" style={style} aria-hidden="true">
      {initials(name)}
    </span>
  );
}

export function Badge({
  children,
  tone,
}: {
  children: ReactNode;
  tone?: 'ok' | 'warn' | 'danger' | 'info';
}) {
  return <span className={`badge${tone ? ` badge--${tone}` : ''}`}>{children}</span>;
}

export function Empty({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="empty__icon" aria-hidden="true">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" strokeLinecap="round" />
        </svg>
      </div>
      <strong>{title}</strong>
      {hint ? <span>{hint}</span> : null}
      {action ? <div style={{ marginTop: 16 }}>{action}</div> : null}
    </div>
  );
}

export function Skeleton({ height = 20, width = '100%' }: { height?: number; width?: number | string }) {
  return <div className="skeleton" style={{ height, width }} />;
}

export function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'pos' | 'neg';
}) {
  return (
    <div className="stat">
      <div className="stat__label">{label}</div>
      <div className={`stat__value tnum${tone ? ` stat__value--${tone}` : ''}`}>{value}</div>
      {hint ? <div className="stat__hint">{hint}</div> : null}
    </div>
  );
}

// ------------------------------------------------------------------ modal

export function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // foco vai para dentro do modal: sem isso o teclado continua na página atrás
    ref.current?.querySelector<HTMLElement>('input, textarea, button')?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <div className="modal__head">
          <h2>{title}</h2>
        </div>
        <div className="modal__body">{children}</div>
        {footer ? <div className="modal__foot">{footer}</div> : null}
      </div>
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {error ? <span className="field__error">{error}</span> : null}
      {!error && hint ? <span className="field__hint">{hint}</span> : null}
    </div>
  );
}
